import type { KennelManagerLabelsInterface } from '../../../KennelManager/types/kennel.types';
import type { AnimalStatus } from '@AnimalModule';
import type { DataTableColumn } from '@DataTableModule';
import type { GenericBooleanInterface } from '@Types';
import { useADialog } from '@Atoms';
import { ROLES } from '@AuthModule';
import { AFDataTableColumn, DATA_TABLE_COMPONENTS, FORMAT_COMPONENTS, getCellComponentHelper } from '@DataTableModule';

const { SYSTEM_MODALS } = useADialog();

export default function (
  field: any,
  specifics: any,
  rules: GenericBooleanInterface,
  _defaultColumnsVisible: any,
  kennelManager: boolean,
  kennelManagerLevelsLabels: KennelManagerLabelsInterface
) {
  const {
    ADDRESS,
    BACKGROUND_CELL,
    DROPDOWN,
    FORMAT,
    IMAGE,
    LINK,
    MULTISELECT,
    HOLD_STATUS_V2,
    OPEN_MODAL_CELL,
    TAG,
    KENNEL_MANAGER_CELL,
  } = DATA_TABLE_COMPONENTS;
  const { BIRTHDAY, DAYS_TO_FIXED, NUMBER_TO_DATE, PHONE, OBJECT_MODEL, ADOPTION_FEE } = FORMAT_COMPONENTS;

  function columnVisible(column: string) {
    return !_defaultColumnsVisible ? false : !_defaultColumnsVisible.includes(column);
  }

  const sortableFields: string[] = [
    'age',
    'altered',
    'altered_in_care',
    'adoption_requests',
    'adoption_fee',
    'animal_name',
    'animal_status',
    'birthday',
    'breed',
    'color_pattern',
    'declawed',
    'euthanasia_reason',
    'kennel',
    'google-address',
    'gender',
    'good_with_cats',
    'good_with_dogs',
    'hold_status',
    'house_trained',
    'id',
    'intake_date',
    'intake_note',
    'intake_type',
    'intake_subtype',
    'jurisdiction',
    'location_status',
    'los',
    'microchip',
    'partner_name',
    'primary_color_map',
    'rabies_tag',
    'return_reason',
    'secondary_breed',
    'seizure_reason',
    'size',
    'species',
    'surrender_reason',
    'temperament',
    'partner_name',
    'weight',
  ];
  const hasSettings = [
    'adoption_fee',
    'intake_type',
    'kennel_site_id',
    'kennel_building_id',
    'kennel_location_id',
    'adoption_requests',
    'animal_name',
    'animal_status',
    'temperament',
    'birthday',
    'google-address',
    'group_litter',
    'hold_status',
    'holds',
    'kennel',
    'kennel_id',
    'location_status',
    'intake_date',
    'los',
    'phone',
    'photo',
    'species',
    'secondary_color_map',
    'tags',
    'temperament',
    'weight',
    'partner_name',
  ];

  const COLUMN: DataTableColumn = new AFDataTableColumn(field, sortableFields.includes(field.name));

  if (!hasSettings.includes(field.name)) return getCellComponentHelper(field.type, COLUMN);
  switch (field.name) {
    case 'group_litter': {
      return {
        ...COLUMN,
        settings: {
          component: TAG,
          type: 'group_litter',
          specifics: specifics.groupLitters,
        },
      };
    }
    case 'tags': {
      return {
        ...COLUMN,
        hidden: columnVisible('tags'),
        settings: {
          component: TAG,
          specifics: specifics.tags,
        },
      };
    }
    case 'adoption_requests': {
      return {
        ...COLUMN,
        hidden: columnVisible('adoption_requests'),
        settings: {
          component: BACKGROUND_CELL,
        },
      };
    }
    case 'location_status': {
      const locations: any[] = specifics.locations;
      // NOTE :: In the future when the internationalization is implemented, this should be changed to the translated values.
      const notEditableValues = ['Foster'];

      return {
        ...COLUMN,
        editable: rules[ROLES.EDIT_ANIMAL] && !notEditableValues.includes(field.value),
        hidden: columnVisible('location_status'),
        settings: {
          component: TAG,
          render: {
            targetKey: 'location_color',
          },
          edit: {
            notEditableValues,
            type: DROPDOWN,
            preserveOptionsOrder: true,
            updateOnEditFields: [
              { key: 'label', field: 'location_status' },
              { key: 'color', field: 'location_color' },
              { key: 'name', field: 'location_name' },
              { key: '_id', field: 'location_id' },
            ],
            options: locations
              .map(({ _id, color, label, name }) => {
                if (['foster'].includes(name)) return false;
                return {
                  label,
                  color,
                  name,
                  _id,
                };
              })
              .filter((isTruthy) => isTruthy),
          },
        },
      };
    }
    case 'holds': {
      return {
        ...COLUMN,
        hidden: columnVisible('holds'),
        settings: {
          component: HOLD_STATUS_V2,
        },
      };
    }
    case 'species': {
      return {
        ...COLUMN,
        hidden: columnVisible('species'),
      };
    }
    case 'google-address': {
      return {
        ...COLUMN,
        settings: {
          component: ADDRESS,
          render: {
            targetKey: 'formatted_address',
          },
        },
      };
    }
    case 'birthday': {
      return {
        ...COLUMN,
        settings: {
          component: FORMAT,
          render: {
            type: BIRTHDAY,
          },
        },
      };
    }
    case 'animal_status': {
      const status: AnimalStatus[] = specifics.statuses;

      return {
        ...COLUMN,
        editable: rules[ROLES.EDIT_ANIMAL],
        settings: {
          component: TAG,
          render: {
            targetKey: 'animal_status_color',
          },
          edit: {
            type: DROPDOWN,
            // NOTE :: In the future when the internationalization is implemented, this should be changed to the translated values.
            notEditableValues: ['Return to Owner', 'Pending Transport', 'Adopted', 'Service Out', 'Clinic Out'],
            updateOnEditFields: [
              { key: 'label', field: 'animal_status' },
              { key: 'color', field: 'animal_status_color' },
              { key: 'name', field: 'animal_status_name' },
              { key: '_id', field: 'animal_status_id' },
            ],
            options: status
              .map(({ _id, color, label, name }) => {
                if (['pending_transport', 'adopted', 'return_to_owner', 'service_out', 'clinic_out'].includes(name))
                  return false;

                return {
                  label,
                  color,
                  name,
                  _id,
                };
              })
              .filter((isTruthy) => isTruthy),
          },
        },
      };
    }
    case 'adoption_fee': {
      return {
        ...COLUMN,
        hidden: columnVisible('adoption_fee'),
        settings: {
          component: FORMAT,
          render: {
            type: ADOPTION_FEE,
            decimal: 'numberDecimal',
          },
        },
      };
    }
    case 'intake_type': {
      return {
        ...COLUMN,
        hidden: columnVisible('intake_type'),
      };
    }
    case 'temperament': {
      return rules[ROLES.EDIT_ANIMAL]
        ? {
            ...COLUMN,
            editable: true,
            settings: {
              component: TAG,
              edit: {
                type: MULTISELECT,
                options: field.values,
              },
            },
          }
        : COLUMN;
    }
    case 'photo': {
      return {
        ...COLUMN,
        settings: {
          component: IMAGE,
          hideImageStatusOverlays: true,
        },
      };
    }
    case 'phone': {
      return {
        ...COLUMN,
        settings: {
          component: FORMAT,
          render: {
            type: PHONE,
          },
        },
      };
    }
    case 'los': {
      return {
        ...COLUMN,
        settings: {
          component: FORMAT,
          render: {
            type: DAYS_TO_FIXED,
          },
        },
      };
    }
    case 'secondary_color_map': {
      return {
        ...COLUMN,
        settings: {
          component: FORMAT,
          render: {
            type: OBJECT_MODEL,
          },
        },
      };
    }
    case 'intake_date': {
      return {
        ...COLUMN,
        settings: {
          component: FORMAT,
          render: {
            type: NUMBER_TO_DATE,
            targetKey: ['$date', '$numberLong'],
          },
        },
      };
    }
    case 'partner_name': {
      return {
        ...COLUMN,
        settings: {
          component: LINK,
          hasName: true,
          render: {
            href: '/shelter/partner/profile/',
            targetKey: ['partner_name', 'id'],
          },
        },
      };
    }
    case 'animal_name': {
      return {
        ...COLUMN,
        settings: {
          canBeUnknown: true,
          component: LINK,
          showStatusIconsBelowAnimalName: true,
          render: {
            href: '/shelter/animal-profile/',
            targetKey: ['animal_id', '$oid'],
          },
        },
      };
    }
    case 'kennel_id': {
      return {
        ...COLUMN,
        editable: false,
        hidden: !kennelManager,
        settings: {
          component: KENNEL_MANAGER_CELL,
          kennelManagerLevelsLabels,
          render: {
            activeModal: SYSTEM_MODALS.ANIMAL_CHANGE_KENNEL_MODAL,
            animalStatusKey: 'status',
            modalProps: {
              isDataTable: true,
            },
          },
        },
      };
    }
    case 'kennel_site_id': {
      return {
        ...COLUMN,
        editable: false,
        hidden: !kennelManager,
        settings: {
          component: KENNEL_MANAGER_CELL,
          kennelManagerLevelsLabels,
          isSite: true,
          render: {
            activeModal: SYSTEM_MODALS.ANIMAL_CHANGE_KENNEL_MODAL,
            animalStatusKey: 'status',
            modalProps: {
              isDataTable: true,
            },
          },
        },
      };
    }
    case 'kennel_building_id': {
      return {
        ...COLUMN,
        editable: false,
        hidden: !kennelManager,
        settings: {
          component: KENNEL_MANAGER_CELL,
          kennelManagerLevelsLabels,
          isBuilding: true,
          render: {
            activeModal: SYSTEM_MODALS.ANIMAL_CHANGE_KENNEL_MODAL,
            animalStatusKey: 'status',
            modalProps: {
              isDataTable: true,
            },
          },
        },
      };
    }
    case 'kennel_location_id': {
      return {
        ...COLUMN,
        editable: false,
        hidden: !kennelManager,
        settings: {
          component: KENNEL_MANAGER_CELL,
          kennelManagerLevelsLabels,
          isLocation: true,
          render: {
            activeModal: SYSTEM_MODALS.ANIMAL_CHANGE_KENNEL_MODAL,
            animalStatusKey: 'status',
            modalProps: {
              isDataTable: true,
            },
          },
        },
      };
    }
    case 'kennel': {
      return {
        ...COLUMN,
        hidden: kennelManager && columnVisible('kennel'),
      };
    }
    case 'weight': {
      return rules[ROLES.EDIT_ANIMAL]
        ? {
            ...COLUMN,
            editable: false,
            settings: {
              component: OPEN_MODAL_CELL,
              isAnimal: true,
              render: {
                activeModal: SYSTEM_MODALS.DATA_TABLE_WEIGHT_MODAL,
              },
            },
          }
        : COLUMN;
    }
    default:
      return COLUMN;
  }
}
